/*
  This component requires some changes to your config:
  
  ```
  // tailwind.config.js
  module.exports = {
    // ...
    plugins: [
      // ...
      require('@tailwindcss/forms'),
    ],
  }
  ```
*/
import { Fragment, useEffect, useState } from "react";
import { Dialog, Menu, Transition } from "@headlessui/react";
import { UserCircleIcon } from "@heroicons/react/24/solid";
import {
  Bars3Icon,
  DocumentDuplicateIcon,
  ClipboardDocumentCheckIcon,
  HomeIcon,
  PlusIcon,
  XMarkIcon,
  ChartPieIcon,
} from "@heroicons/react/24/outline";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
//import { useAuth } from "../../context/AuthContext";
import { useAuth } from "../../context/AuthContext_V2";
import { useLocation, Link } from "react-router-dom";
import { useCreateReportMachine } from "../../context/CreateReportMachineContext";
import { useCreateActionPlanMachine } from "../../context/action-plans/CreateActionPlanMachineContext";
import { useViewEditReportMachine } from "../../context/ViewEditReportMachineContext";
import { useCreateCorrectiveActionMachine } from "../../context/action-plans/CreateCorrectiveActionMachineContext";
import { useTranslation } from "react-i18next";
import LanguageMenu from "../ui/LanguageMenu";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export default function Navigation({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { keycloak, state, send, profile } = useAuth();
  const location = useLocation();
  const regexActionPlan = /\/action-plans\/\d+/;

  const [createReportState, createReportSend] = useCreateReportMachine();
  const [viewEditReportState, viewEditReportSend] = useViewEditReportMachine();
  const [createActionPlanState, createActionPlanSend] =
    useCreateActionPlanMachine();
  const [createCorrectiveActionState, createCorrectiveActionSend] =
    useCreateCorrectiveActionMachine();

  const { t } = useTranslation();

  const auditToolUrl =
    process.env.REACT_APP_AUDIT_TOOL_FRONTEND_URL ||
    "https://audit-tool-v1.local.orpea-belgium.com:446/";

  const navigation = [
    {
      name: t("homepage.navigation.dashboard"),
      href: "/",
      navEvent: "GO_TO_HOME",
      icon: HomeIcon,
      current: true,
    },
    {
      name: t("homepage.navigation.reports"),
      href: "/reports",
      navEvent: "GO_TO_REPORTS",
      icon: DocumentDuplicateIcon,
      current: false,
    },
    {
      name: t("homepage.navigation.actionPlans"),
      href: "/action-plans",
      navEvent: "GO_TO_ACTION_PLANS",
      icon: ClipboardDocumentCheckIcon,
      current: false,
    },
    {
      name: t("homepage.navigation.audits"),
      href: auditToolUrl,
      //target: "audit-tool",
      navEvent: "GO_TO_AUDITS",
      icon: ChartPieIcon,
      current: false,
    },
  ];

  useEffect(() => {
    console.log("createActionPlanState", createActionPlanState.value);
  }, [createActionPlanState.value]);

  return (
    <>
      <div>
        <Transition.Root show={sidebarOpen} as={Fragment}>
          <Dialog
            as="div"
            className="relative z-50 lg:hidden"
            onClose={setSidebarOpen}
          >
            <Transition.Child
              as={Fragment}
              enter="transition-opacity ease-linear duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="transition-opacity ease-linear duration-300"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-gray-900/80" />
            </Transition.Child>

            <div className="fixed inset-0 flex">
              <Transition.Child
                as={Fragment}
                enter="transition ease-in-out duration-300 transform"
                enterFrom="-translate-x-full"
                enterTo="translate-x-0"
                leave="transition ease-in-out duration-300 transform"
                leaveFrom="translate-x-0"
                leaveTo="-translate-x-full"
              >
                <Dialog.Panel className="relative mr-16 flex w-full max-w-xs flex-1">
                  <Transition.Child
                    as={Fragment}
                    enter="ease-in-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in-out duration-300"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <div className="absolute left-full top-0 flex w-16 justify-center pt-5">
                      <button
                        type="button"
                        className="-m-2.5 p-2.5"
                        onClick={() => setSidebarOpen(false)}
                      >
                        <span className="sr-only">Close sidebar</span>
                        <XMarkIcon
                          className="h-6 w-6 text-white"
                          aria-hidden="true"
                        />
                      </button>
                    </div>
                  </Transition.Child>
                  {/* Sidebar component, swap this element with another sidebar if you like */}
                  <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-indigo-800 bg-radial-gradient px-6 pb-4">
                    <div className="flex h-16 shrink-0 items-center">
                      {/* <img
                        className="h-8 w-auto"
                        src="https://tailwindui.com/img/logos/mark.svg?color=white"
                        alt="Your Company"
                      /> */}
                    </div>
                    <nav className="flex flex-1 flex-col">
                      <ul role="list" className="flex flex-1 flex-col gap-y-7">
                        <li>
                          <ul role="list" className="-mx-2 space-y-1">
                            {navigation.map((item) => (
                              <li key={item.name}>
                                <Link
                                  to={item.href}
                                  target={item.target || "_self"}
                                  rel={item.target ? "noopener noreferrer" : ""}
                                  className={classNames(
                                    item.href === location.pathname
                                      ? "bg-indigo-700 text-white"
                                      : "text-indigo-200 hover:bg-indigo-700 hover:text-white",
                                    "group flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6 ",
                                  )}
                                >
                                  <item.icon
                                    className={classNames(
                                      item.href === location.pathname
                                        ? "text-white"
                                        : "text-indigo-200 group-hover:text-white",
                                      "h-6 w-6 shrink-0",
                                    )}
                                    aria-hidden="true"
                                  />
                                  {item.name}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </li>
                      </ul>
                    </nav>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </Dialog>
        </Transition.Root>

        {/* Static sidebar for desktop */}
        <div className="hidden print:hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-52 lg:flex-col">
          {/* Sidebar component, swap this element with another sidebar if you like */}
          <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-indigo-800 bg-radial-gradient px-6 pb-4">
            <div className="flex h-16 shrink-0 items-center">
              {/* <img
                className="h-8 w-auto"
                src="https://tailwindui.com/img/logos/mark.svg?color=white"
                alt="Your Company"
              /> */}
            </div>
            <nav className="flex flex-1 flex-col">
              <ul role="list" className="flex flex-1 flex-col gap-y-7">
                <li>
                  <ul role="list" className="-mx-2 space-y-1">
                    {navigation.map((item) => (
                      <li key={item.name}>
                        <Link
                          to={item.href}
                          target={item.target || "_self"}
                          rel={item.target ? "noopener noreferrer" : ""}
                          className={classNames(
                            item.href === location.pathname
                              ? "bg-indigo-700 text-white"
                              : "text-indigo-200 hover:bg-indigo-700 hover:text-white",
                            "group flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6",
                          )}
                        >
                          <item.icon
                            className={classNames(
                              item.href === location.pathname
                                ? "text-white"
                                : "text-indigo-200 group-hover:text-white",
                              "h-6 w-6 shrink-0",
                            )}
                            aria-hidden="true"
                          />
                          {item.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </li>

                {/* <li className="mt-auto">
                  <a
                    href="#"
                    className="group -mx-2 flex gap-x-3 rounded-md p-2 text-sm font-semibold leading-6 text-indigo-200 hover:bg-indigo-700 hover:text-white"
                  >
                    <Cog6ToothIcon
                      className="h-6 w-6 shrink-0 text-indigo-200 group-hover:text-white"
                      aria-hidden="true"
                    />
                    Settings
                  </a>
                </li> */}
              </ul>
            </nav>
          </div>
        </div>

        <div className=" lg:pl-52">
          <div className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-gray-200 bg-white px-4 shadow-sm print:hidden sm:gap-x-6 sm:px-6 lg:px-8">
            <button
              type="button"
              className="-m-2.5 p-2.5 text-gray-700 lg:hidden"
              onClick={() => setSidebarOpen(true)}
            >
              <span className="sr-only">Open sidebar</span>
              <Bars3Icon className="h-6 w-6" aria-hidden="true" />
            </button>

            {/* Separator */}
            <div
              className="h-6 w-px bg-gray-900/10 lg:hidden"
              aria-hidden="true"
            />

            <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
              <div className="relative flex flex-1"></div>
              {/* <form className="relative flex flex-1" action="#" method="GET">
                <label htmlFor="search-field" className="sr-only">
                  Search
                </label>
                <MagnifyingGlassIcon
                  className="pointer-events-none absolute inset-y-0 left-0 h-full w-5 text-gray-400"
                  aria-hidden="true"
                />
                <input
                  id="search-field"
                  className="block h-full w-full border-0 py-0 pl-8 pr-0 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm"
                  placeholder="Search..."
                  type="search"
                  name="search"
                />
              </form> */}
              <div className="flex items-center gap-x-4 lg:gap-x-6">
                {/* <button type="button" className="-m-2.5 p-2.5 text-gray-400 hover:text-gray-500">
                  <span className="sr-only">View notifications</span>
                  <BellIcon className="h-6 w-6" aria-hidden="true" />
                </button> */}
                <div className="flex-shrink-0">
                  {(location.pathname.startsWith("/reports") ||
                    location.pathname === "/") && (
                    <button
                      type="button"
                      className="relative inline-flex items-center gap-x-1.5 rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500 disabled:cursor-not-allowed disabled:bg-gray-400 "
                      onClick={() => {
                        createReportSend("START");
                      }}
                      disabled={
                        !createReportState.can("START") ||
                        viewEditReportState.matches(
                          "processing.initialized.active.edit",
                        )
                      }
                    >
                      <PlusIcon
                        className="-ml-0.5 h-5 w-5"
                        aria-hidden="true"
                      />
                      {t("homepage.button.createReport")}
                    </button>
                  )}
                  {location.pathname === "/action-plans" && (
                    <button
                      type="button"
                      className="relative inline-flex items-center gap-x-1.5 rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500 disabled:cursor-not-allowed disabled:bg-gray-400 "
                      onClick={() => {
                        console.log("createActionPlanSend");
                        createActionPlanSend("START", {
                          data: {
                            authentication: {
                              token: keycloak.token,
                            },
                          },
                        });
                      }}
                    >
                      <PlusIcon
                        className="-ml-0.5 h-5 w-5"
                        aria-hidden="true"
                      />
                      {t("homepage.button.createActionPlan")}
                    </button>
                  )}
                  {regexActionPlan.test(location.pathname) && (
                    <button
                      type="button"
                      className="relative inline-flex items-center gap-x-1.5 rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500 disabled:cursor-not-allowed disabled:bg-gray-400 "
                      onClick={() => {
                        createCorrectiveActionSend({
                          type: "START",
                          data: {
                            authentication: { token: keycloak.token },
                            actionPlanId: location.pathname.split("/")[2],
                          },
                        });
                      }}
                      disabled={
                        !createCorrectiveActionState.can({
                          type: "START",
                          data: {
                            authentication: { token: keycloak.token },
                            actionPlanId: location.pathname.split("/")[2],
                          },
                        })
                      }
                    >
                      <PlusIcon
                        className="-ml-0.5 h-5 w-5"
                        aria-hidden="true"
                      />
                      {t("homepage.button.createAction")}
                    </button>
                  )}
                </div>
                {/* Separator */}
                <div
                  className="hidden lg:block lg:h-6 lg:w-px lg:bg-gray-900/10"
                  aria-hidden="true"
                />

                {/* Profile dropdown */}
                <Menu as="div" className="relative">
                  <Menu.Button className="-m-1.5 flex items-center p-1.5">
                    <span className="sr-only">Open user menu</span>
                    {/* <img
                      className="h-8 w-8 rounded-full bg-gray-50"
                      src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80"
                      alt=""
                    /> */}
                    <UserCircleIcon
                      className="h-8 w-8 text-gray-400"
                      aria-hidden="true"
                    />
                    <span className="hidden lg:flex lg:items-center">
                      <span
                        className="ml-4 text-sm font-medium leading-6 text-gray-900"
                        aria-hidden="true"
                      >
                        {profile?.firstName} {profile?.lastName}
                      </span>
                      <ChevronDownIcon
                        className="ml-2 h-5 w-5 text-gray-400"
                        aria-hidden="true"
                      />
                    </span>
                  </Menu.Button>
                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                  >
                    <Menu.Items className="absolute right-0 z-10 mt-2.5 origin-top-right rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5 focus:outline-none">
                      <Menu.Item>
                        <a
                          onClick={(evt) => {
                            evt.preventDefault();
                            send("LOGOUT");
                          }}
                          className={classNames(
                            "cursor-pointer",
                            "block px-3 py-1 text-sm leading-6 text-red-600",
                          )}
                        >
                          {t("homepage.profile.signOut")}
                        </a>
                      </Menu.Item>
                    </Menu.Items>
                  </Transition>
                </Menu>
                {/* Separator */}
                <div
                  className="hidden lg:block lg:h-6 lg:w-px lg:bg-gray-900/10"
                  aria-hidden="true"
                />

                <LanguageMenu />
              </div>
            </div>
          </div>

          <main className="py-10">
            <div className="px-4 sm:px-6 lg:px-8">{children}</div>
          </main>
        </div>
      </div>
    </>
  );
}
